/**
 * Mutations often read before they write (fetch the current version, check
 * it). A failure of those reads means nothing was sent; only a failure of the
 * write itself can hide a committed change. Wrap the write with `sent(...)`
 * and ask `wasSent(error)` before telling the user "it may already have
 * happened".
 */
import { getSessionSnapshot } from "@/features/auth/session";

const sentErrors = new WeakSet<object>();
const currentUser = () => getSessionSnapshot().userId;
// Failures the request layer raised before dispatching anything (token
// refresh failed, the session changed while waiting): nothing reached the
// server even though the write was called.
const undispatched = new WeakSet<object>();

export function markNotDispatched(error: unknown): void {
  if (error && typeof error === "object") undispatched.add(error);
}

/** The request layer failed before anything left the browser. */
export function wasNotDispatched(error: unknown): boolean {
  return !!error && typeof error === "object" && undispatched.has(error);
}

// Who was signed in when the write was sent (a late failure may arrive
// after another tab switched users).
const owners = new WeakMap<object, string | null>();

export async function sent<T>(request: Promise<T>, owner: string | null = currentUser()): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (error && typeof error === "object") {
      sentErrors.add(error);
      owners.set(error, owner);
    }
    throw error;
  }
}

/** The user a dispatched write was sent as (undefined when not via sent()). */
export function sentAs(error: unknown): string | null | undefined {
  return error && typeof error === "object" ? owners.get(error) : undefined;
}

/**
 * The write left the browser, whoever is signed in now. Only for records kept
 * per sending user (e.g. unseen-token flags stored under `sentAs(error)`).
 */
export function wasDispatched(error: unknown): boolean {
  return !!error && typeof error === "object" && sentErrors.has(error) && !undispatched.has(error);
}

/**
 * The write left the browser, and for the user signed in now: a failure that
 * lands after another user signed in belongs to the previous session, so it
 * must not reach this one as "may already have happened" (toasts, holds,
 * cache refreshes in the new user's store).
 */
export function wasSent(error: unknown): boolean {
  return (
    !!error &&
    typeof error === "object" &&
    sentErrors.has(error) &&
    !undispatched.has(error) &&
    owners.get(error) === currentUser()
  );
}

// Errors already reported to the user as "may already have happened": the
// write was dispatched and its result is unknown. A confirm dialog closes on
// these instead of inviting the user to confirm the same action again.
const outcomeUnknown = new WeakSet<object>();

export function markOutcomeUnknown(error: unknown): void {
  if (error && typeof error === "object") outcomeUnknown.add(error);
}

export function isOutcomeUnknown(error: unknown): boolean {
  return !!error && typeof error === "object" && outcomeUnknown.has(error);
}
